import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthStore } from "../state/authStore";
import { childrenApi, Child, Progress } from "../api/children";
import { useSessionSocket } from "../hooks/useSessionSocket";
import { sessionsApi, ActiveSession } from "../api/sessions";
import { pairingApi, TvDevice } from "../api/pairing";
import { subscriptionsApi } from "../api/subscriptions";
import { Icon } from "./components/Icon";
import { WebMascot } from "./components/WebMascot";
import { prettyTitle } from "./prettyTitle";
import { WebHome } from "./screens/WebHome";
import { WebControls } from "./screens/WebControls";
import { WebActivity } from "./screens/WebActivity";
import { WebStickerBook } from "./screens/WebStickerBook";
import { WebCoWatch } from "./screens/WebCoWatch";
import {
  WebFamilyAccount,
  emailFromJwt,
} from "./screens/WebFamilyAccount";
import { WebPlaylists } from "./screens/WebPlaylists";
import { WebAdminMetrics } from "./screens/WebAdminMetrics";
import { WebPlayer } from "./screens/WebPlayer";
import { WebStudio } from "./screens/WebStudio";
import { WebSeriesDetail } from "./screens/WebSeriesDetail";
import { WebVideo } from "./components/WebVideoCard";
import { getHashPath, setHashPath, subscribeHash } from "./webRoute";
import { webAlert, webConfirm, WebDialogHost } from "./webDialog";

// Routes the web app cycles through. `studio` is the new AI Family
// Content Generator — promoted to top nav since it's the headline
// differentiator. The old Questions admin still exists; access it from
// Me → Content admin if needed.
export type WebView =
  | "home"
  | "studio"
  | "series"
  | "playlists"
  | "cowatch"
  | "controls"
  | "activity"
  | "awards"
  | "me"
  | "player"
  // Internal / founder operational dashboard. Reachable from a small
  // link in the Family Account privacy tab — backend AdminGuard is the
  // real gate (env-based ADMIN_EMAILS allowlist). Hidden from the main
  // nav because most parents never see it.
  | "adminMetrics";

// view ↔ URL-hash map. `home` is the bare "/" so the app root is Home.
// `player` is transient (needs an activeVideo), so it isn't deep-linkable —
// a cold load on /player falls back to Home.
const VIEW_TO_PATH: Record<WebView, string> = {
  home: "/",
  studio: "/studio",
  series: "/series",
  playlists: "/playlists",
  cowatch: "/cowatch",
  controls: "/controls",
  activity: "/activity",
  awards: "/stickers",
  me: "/me",
  player: "/player",
  adminMetrics: "/admin",
};
function pathToView(path: string): WebView | null {
  const keys = Object.keys(VIEW_TO_PATH) as WebView[];
  return keys.find((v) => VIEW_TO_PATH[v] === path) ?? null;
}

const NAV: { key: WebView; label: string }[] = [
  { key: "home", label: "Home" },
  { key: "studio", label: "Studio" },
  { key: "playlists", label: "Playlists" },
  { key: "cowatch", label: "Co-watch" },
  { key: "controls", label: "Controls" },
  { key: "activity", label: "Activity" },
  { key: "awards", label: "Stickers" },
];

const PROFILE_THEMES = [
  { color: "#F18EB6", bg: "#FFE0EB" },
  { color: "#5B8DEF", bg: "#DCE7FF" },
  { color: "#7AC74F", bg: "#DEF1CF" },
  { color: "#FF8A4C", bg: "#FFE2C2" },
  { color: "#8C7DD9", bg: "#E6E0F7" },
];

export type ChildWithProgress = Child & {
  progress?: Progress;
  theme?: { color: string; bg: string };
};

export type ActiveQuestion = {
  id: string;
  prompt: string;
  options: string[];
  correct: number;
  // Optional live answer state mirrored from the TV.
  selectedOption?: number;
  childAnsweredCorrectly?: boolean;
};

export type NowPlaying = {
  sessionId: string;
  childId: string;
  videoTitle: string;
  videoCategory?: string;
  durationSec?: number;
  progress?: number; // 0..1, live from TV heartbeats every 2s
  secondsWatched?: number;
  paused?: boolean;
  activeQuestion?: ActiveQuestion | null;
  // Multi-TV: which device this session is playing on. Stamped server-side
  // on every tv_event. May be 'legacy' for pre-rollout TVs, or undefined
  // when hydrated from REST (the active-sessions endpoint doesn't carry
  // tvId yet — heartbeat will fill it in).
  tvId?: string;
};

function activeSessionToNowPlaying(s: ActiveSession): NowPlaying {
  const dur = s.durationSec || 0;
  return {
    sessionId: s.sessionId,
    childId: s.childId,
    videoTitle: s.videoTitle,
    videoCategory: s.videoCategory ?? undefined,
    durationSec: dur,
    secondsWatched: s.secondsWatched,
    progress: dur > 0 ? Math.min(1, s.secondsWatched / dur) : 0,
    paused: false,
    activeQuestion: null,
  };
}

// Self-heal: older auth payloads didn't include parentId. Decode the JWT
// sub claim to recover it without forcing a logout.
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = typeof atob === "function" ? atob(padded) : "";
    const payload = JSON.parse(decoded);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

type Nav = {
  navigate: (screen: string, params?: Record<string, unknown>) => void;
  replace: (screen: string, params?: Record<string, unknown>) => void;
  goBack: () => void;
  canGoBack: () => boolean;
};

export function WebShell({ nav }: { nav: Nav }) {
  const {
    accessToken,
    parentId,
    setParentId,
    logout,
    subscription,
    setSubscription,
    _hydrated,
  } = useAuthStore();

  const [view, setView] = useState<WebView>("home");

  // ── URL-hash routing ──────────────────────────────────────────────────
  // Reflect the active tab in the URL hash (e.g. #/activity) so the address
  // bar changes per page, browser back/forward navigate, and refresh /
  // deep-links restore the page. See src/web/webRoute.ts for the why.
  const routeSynced = useRef(false);
  // Adopt the page from the URL on mount (refresh / deep-link).
  useEffect(() => {
    const v = pathToView(getHashPath());
    if (v === "player" || v === "series") {
      // Transient — both need an in-memory selection (activeVideo /
      // activeSeriesId), so a cold load falls back to Home.
      setHashPath("/", true);
    } else if (v) {
      setView(v);
    } else {
      // Unknown path when the shell mounts — usually left over from an outer
      // screen (e.g. /login right after sign-in). Align the URL with the
      // landing view (home) instead of leaving a stale /login in the bar.
      setHashPath("/", true);
    }
  }, []);
  // view → hash. Skip the first run so the mount-adopt above isn't
  // clobbered before its setView commits.
  useEffect(() => {
    if (!routeSynced.current) {
      routeSynced.current = true;
      return;
    }
    setHashPath(VIEW_TO_PATH[view]);
  }, [view]);
  // hash → view (browser back / forward).
  useEffect(
    () =>
      subscribeHash(() => {
        const v = pathToView(getHashPath());
        if (v) setView((cur) => (v !== cur ? v : cur));
      }),
    [],
  );

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [children, setChildren] = useState<ChildWithProgress[]>([]);
  // Bumped after a kid is renamed / deleted so the roster re-fetches in place.
  // A full page reload would land on the current sub-route and re-run SSR,
  // which is a worse experience than just refetching the list.
  const [kidsVersion, setKidsVersion] = useState(0);
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  // Session id the LOCAL web player is itself playing. The backend echoes our
  // own playback back over the socket as `session_started`; without tracking
  // it we'd render our own web session as a co-watch banner ("watching on the
  // TV") pointing at ourselves. Ref is read inside the memoized socket handler;
  // state drives the banner re-render.
  const localSessionIdRef = useRef<string | null>(null);
  const [localSessionId, setLocalSessionId] = useState<string | null>(null);
  const handleLocalSession = useCallback((sid: string | null) => {
    localSessionIdRef.current = sid;
    setLocalSessionId(sid);
    // If our own session already landed in nowPlaying (socket beat this
    // callback), drop it now so the banner never flashes.
    if (sid)
      setNowPlaying((prev) =>
        prev && prev.sessionId === sid ? null : prev,
      );
  }, []);
  const [activeVideo, setActiveVideo] = useState<WebVideo | null>(null);
  // TV shows: the show whose info page is open, and the ordered-episode queue
  // that drives auto-advance while an episode plays.
  const [activeSeriesId, setActiveSeriesId] = useState<string | null>(null);
  const [episodeQueue, setEpisodeQueue] = useState<WebVideo[] | null>(null);
  // View to return to when the player closes (set by onPlay to the screen
  // playback was launched from). Defaults to Home.
  const [playerReturnView, setPlayerReturnView] = useState<WebView>("home");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tvStatus, setTvStatus] = useState<{
    paired: boolean;
    online: boolean;
  }>({ paired: false, online: false });
  // Multi-TV: per-device list. Drives the dropdown rows. Kept in sync via
  // (a) initial GET /v1/pairing/devices, (b) refresh on every tv_status
  // event (per-device or aggregate), and (c) optimistic edits when the
  // parent disconnects a row from this UI.
  const [devices, setDevices] = useState<TvDevice[]>([]);
  const [tvMenuOpen, setTvMenuOpen] = useState(false);
  // Mobile hamburger drawer (replaces the horizontal nav under 768px).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const profileBtnRef = useRef<HTMLDivElement>(null);
  const tvPillRef = useRef<HTMLDivElement>(null);

  // Guest browsing: a logged-OUT visitor can still browse free videos on
  // the web — we no longer bounce them to Login. The shell renders a guest
  // variant (home-only nav, Sign in / Sign up in the topbar) and every data
  // effect below already no-ops without an accessToken, so nothing 401-spams.
  //
  // Gate purely on `accessToken` (not the store's `_hydrated`, which is
  // unreliable on the Next/web build): WebApp already returns null until it
  // has read auth from localStorage, and the persisted store hydrates
  // synchronously before first paint, so a logged-in user's token is present
  // by the time this renders — no guest flash.
  const isGuest = !accessToken;

  // Heal missing parentId from JWT once.
  useEffect(() => {
    if (parentId || !accessToken) return;
    const sub = decodeJwtSub(accessToken);
    if (sub) setParentId(sub);
  }, [parentId, accessToken, setParentId]);

  // Hydrate Now Playing on mount / login from REST. Without this, opening
  // (or refreshing) the parent web AFTER the TV started a session would
  // miss the one-shot `session_started` socket event and show the empty
  // state forever. We only adopt the REST snapshot when we don't already
  // have a live socket-driven `nowPlaying` to avoid clobbering fresh data.
  const hydrateNowPlaying = useCallback(
    async (sessionIdHint?: string) => {
      if (!accessToken) return;
      try {
        const { sessions } = await sessionsApi.active(accessToken);
        const match =
          (sessionIdHint &&
            sessions.find((s) => s.sessionId === sessionIdHint)) ||
          sessions[0];
        // Nothing active — clear the bar rather than leaving whatever was
        // there. Returning early here is what kept a stale Now Playing bar on
        // screen after the session had already ended.
        if (!match) {
          setNowPlaying(null);
          return;
        }
        setNowPlaying((prev) => {
          if (prev && prev.sessionId === match.sessionId) return prev;
          return activeSessionToNowPlaying(match);
        });
      } catch {
        // Non-fatal: live events will recover on the next session_started.
      }
    },
    [accessToken],
  );

  useEffect(() => {
    hydrateNowPlaying();
  }, [hydrateNowPlaying]);

  // Multi-TV device list refresh. Hits GET /v1/pairing/devices. If the
  // endpoint isn't available (older backend) or returns empty but /status
  // still says paired:true, we synthesize a single legacy device entry so
  // the dropdown still has something to render for parents who paired
  // before the multi-TV rollout.
  const refreshDevices = useCallback(async () => {
    if (!accessToken) return;
    try {
      const list = await pairingApi.devices(accessToken);
      if (Array.isArray(list) && list.length > 0) {
        setDevices(list);
        return;
      }
      // No rows returned. Fall back to /status — if it claims paired, we
      // know there's *some* legacy TV out there even if /devices can't
      // describe it.
      const status = await pairingApi.status(accessToken).catch(() => null);
      if (status?.paired) {
        setDevices([
          {
            id: "__legacy__",
            name: "TV 1",
            claimedAt: new Date().toISOString(),
            online: !!status.online,
          },
        ]);
      } else {
        setDevices([]);
      }
    } catch {
      // /devices threw — likely an older backend without the endpoint.
      // Synthesize from /status so legacy paired TVs still render.
      try {
        const status = await pairingApi.status(accessToken);
        setDevices(
          status.paired
            ? [
                {
                  id: "__legacy__",
                  name: "TV 1",
                  claimedAt: new Date().toISOString(),
                  online: !!status.online,
                },
              ]
            : [],
        );
      } catch {
        setDevices([]);
      }
    }
  }, [accessToken]);

  // Initial pairing snapshot — the gateway will keep this fresh via the
  // `tv_status` socket event after this point. We also fan out to the
  // multi-TV device list here so the dropdown is populated on first paint.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    pairingApi
      .status(accessToken)
      .then((s) => {
        if (!cancelled) setTvStatus(s);
      })
      .catch(() => {});
    refreshDevices();
    return () => {
      cancelled = true;
    };
  }, [accessToken, refreshDevices]);

  // Subscription entitlement. Hits the DB-only `/me` endpoint on shell
  // mount + once more if we just bounced back from Stripe (the URL gains
  // ?subscription=success|cancel|managed after a redirect). The webhook
  // is the actual source of truth — this poll catches us up in the
  // ~seconds-of-race between checkout completion and webhook delivery.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const refresh = () =>
      subscriptionsApi
        .me(accessToken)
        .then(async (s) => {
          if (cancelled) return;
          // A guest who tapped "Start free trial" gets bounced through
          // signup; we stashed the intent in localStorage. Now that they're
          // authed, auto-activate the trial (if still eligible) and clear the
          // flag. The backend sends the welcome email on grant.
          const pending =
            typeof window !== "undefined" &&
            window.localStorage.getItem("kidly-pending-trial") === "1";
          if (pending && s.canStartFreeTrial) {
            window.localStorage.removeItem("kidly-pending-trial");
            try {
              const next = await subscriptionsApi.startTrial(accessToken);
              if (!cancelled) setSubscription(next);
              return;
            } catch {
              /* fall through to the plain status below */
            }
          } else if (pending) {
            // Not eligible (already trialed/subscribed) — drop the stale flag.
            window.localStorage.removeItem("kidly-pending-trial");
          }
          setSubscription(s);
        })
        .catch(() => {});
    refresh();
    // If we just got back from Stripe, wait a beat then re-fetch — Stripe's
    // webhook fires within ~2-5s of checkout completion in practice.
    if (
      typeof window !== "undefined" &&
      /[?&]subscription=/.test(window.location.search)
    ) {
      const id = setTimeout(refresh, 3500);
      return () => {
        cancelled = true;
        clearTimeout(id);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [accessToken, setSubscription]);

  // Load children + their progress.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await childrenApi.list(accessToken);
        const withProgress = await Promise.all(
          list.map(async (child, i) => {
            const theme = PROFILE_THEMES[i % PROFILE_THEMES.length];
            try {
              const progress = await childrenApi.getProgress(
                child.id,
                accessToken,
              );
              return { ...child, progress, theme };
            } catch {
              return { ...child, theme };
            }
          }),
        );
        if (cancelled) return;
        setChildren(withProgress);
        // Only (re)pick a default when the current selection is gone — a
        // refetch after an edit shouldn't yank the parent back to the first kid.
        setActiveChildId((prev) =>
          prev && withProgress.some((c) => c.id === prev)
            ? prev
            : (withProgress[0]?.id ?? null),
        );
        setLoadError(null);
      } catch (e: any) {
        if (cancelled) return;
        // 401 means our cached token is expired or signed by a different
        // backend instance. Clean up so the user sees Login, not a confusing
        // empty shell.
        if (e?.status === 401) {
          logout();
          nav.replace("Login");
          return;
        }
        setChildren([]);
        setLoadError(
          e?.message
            ? `Couldn't load profiles: ${e.message}`
            : "Couldn't load profiles",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, logout, nav, kidsVersion]);

  // Live data TV → parent web. We mirror enough state to render the
  // Co-watch view in near real-time: progress, paused, and any active
  // question (with the child's selected option once they answer).
  useSessionSocket({
    parentId: parentId ?? null,
    role: "parent",
    onTvEvent: useCallback(
      (event: any) => {
        const d = event.data ?? {};
        // ANY tv_event proves a TV is paired and reachable — latch
        // `paired: true` so the topbar reflects reality even if the
        // initial REST snapshot or `tv_status` event was missed.
        setTvStatus((prev) => (prev.paired ? prev : { ...prev, paired: true }));
        // Multi-TV: backend stamps tvId on every tv_event from the
        // originating socket's handshake. May be 'legacy' for pre-rollout
        // TVs. Captured here so the menu can scope "Stop session" to the
        // specific TV that started the session.
        const tvId: string | undefined =
          typeof event.tvId === "string" ? event.tvId : undefined;
        switch (event.type) {
          case "session_started":
            if (!d.sessionId) return;
            // Our own local web playback is echoed back to us — ignore it so
            // it doesn't surface as a "watching on the TV" co-watch banner.
            if (d.sessionId === localSessionIdRef.current) return;
            setNowPlaying({
              sessionId: d.sessionId,
              childId: event.childId ?? d.childId ?? "",
              videoTitle: d.videoTitle ?? "Now playing",
              videoCategory: Array.isArray(d.categories)
                ? d.categories[0]
                : undefined,
              durationSec: d.durationSec,
              progress: 0,
              secondsWatched: 0,
              paused: false,
              activeQuestion: null,
              tvId,
            });
            break;
          case "session_ended":
            setNowPlaying(null);
            break;
          case "generation_ready": {
            const title =
              typeof d.videoTitle === "string"
                ? d.videoTitle
                : "Your custom cartoon";
            if (
              typeof window !== "undefined" &&
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              new Notification("Cartoon ready", {
                body: `"${title}" is ready in Kidly Studio.`,
              });
            } else if (typeof window !== "undefined") {
              webAlert(`"${title}" is ready in Kidly Studio.`);
            }
            break;
          }
          case "heartbeat":
            setNowPlaying((prev) => {
              if (!prev || prev.sessionId !== d.sessionId) {
                // Heartbeat arrived for a session we don't know about — we
                // missed `session_started` (parent opened web mid-playback,
                // or socket reconnected). Pull the snapshot via REST so the
                // Co-watch surface fills in instead of staying blank.
                if (typeof d.sessionId === "string") {
                  hydrateNowPlaying(d.sessionId);
                }
                return prev;
              }
              return {
                ...prev,
                progress:
                  typeof d.progress === "number" ? d.progress : prev.progress,
                secondsWatched:
                  typeof d.secondsWatched === "number"
                    ? d.secondsWatched
                    : prev.secondsWatched,
                paused: typeof d.paused === "boolean" ? d.paused : prev.paused,
                // Late-bind tvId if it was missing (e.g. REST hydration
                // path doesn't carry it). Don't clobber an existing value.
                tvId: prev.tvId ?? tvId,
              };
            });
            break;
          case "question_shown": {
            if (!d.question) return;
            // Options can arrive in three shapes depending on data age:
            //   - string[]                       (legacy seed rows)
            //   - { label, iconKey? }[]          (current schema)
            //   - stringified JSON of either     (some WebSocket relays)
            // The Co-watch overlay only renders the label, so flatten to
            // a plain string[] here — otherwise React tries to render the
            // raw object and throws "Objects are not valid as a React child".
            const rawOpts: unknown = Array.isArray(d.question.options)
              ? d.question.options
              : (() => {
                  try {
                    const p = JSON.parse(d.question.options);
                    return Array.isArray(p) ? p : [];
                  } catch {
                    return [];
                  }
                })();
            const parsedOpts: string[] = (rawOpts as unknown[]).map((o) => {
              if (typeof o === "string") return o;
              if (o && typeof o === "object" && "label" in o) {
                const v = (o as { label?: unknown }).label;
                return typeof v === "string" ? v : "";
              }
              return String(o);
            });
            setNowPlaying((prev) =>
              prev && prev.sessionId === d.sessionId
                ? {
                    ...prev,
                    activeQuestion: {
                      id: d.question.id,
                      prompt: d.question.prompt,
                      options: parsedOpts,
                      correct: d.question.correct,
                    },
                  }
                : prev,
            );
            break;
          }
          case "answer_submitted":
            setNowPlaying((prev) =>
              prev &&
              prev.sessionId === d.sessionId &&
              prev.activeQuestion &&
              prev.activeQuestion.id === d.questionId
                ? {
                    ...prev,
                    activeQuestion: {
                      ...prev.activeQuestion,
                      selectedOption: d.selectedOption,
                      childAnsweredCorrectly: !!d.correct,
                    },
                  }
                : prev,
            );
            break;
          case "question_dismissed":
            setNowPlaying((prev) =>
              prev &&
              prev.sessionId === d.sessionId &&
              prev.activeQuestion?.id === d.questionId
                ? { ...prev, activeQuestion: null }
                : prev,
            );
            break;
        }
      },
      [hydrateNowPlaying],
    ),
    onTvStatus: useCallback(
      (status: { online: boolean; tvId?: string }) => {
        // Backend now emits tv_status in two flavors over the same channel:
        //   - Per-device: { parentId, tvId, online } — drives the per-row dot.
        //   - Aggregate:  { parentId, online }       — drives the pill.
        // We dispatch on the presence of tvId. The aggregate flavor is also
        // what a late-joining parent socket gets as a snapshot on connect.
        if (typeof status.tvId === "string") {
          setDevices((prev) => {
            const idx = prev.findIndex((d) => d.id === status.tvId);
            if (idx === -1) {
              // Unknown device flipped online/offline — refresh from REST
              // to learn its name/claimedAt. Don't synthesize a placeholder
              // here since we'd just be guessing.
              refreshDevices();
              return prev;
            }
            const next = prev.slice();
            next[idx] = { ...next[idx], online: status.online };
            return next;
          });
          // Keep the aggregate pill in sync: any device online ⇒ online.
          setTvStatus((prev) => {
            // We can compute aggregate cheaply by reading current devices,
            // but we don't want a stale closure. Use a follow-up effect via
            // setDevices' next render is awkward — simpler to just latch
            // online when this device went online, and rely on the next
            // aggregate tv_status to flip it back to false.
            if (status.online) return { paired: true, online: true };
            return prev;
          });
        } else {
          // Aggregate flavor — preserves legacy single-pill behavior.
          setTvStatus((prev) => ({
            paired: status.online ? true : prev.paired,
            online: status.online,
          }));
          // Aggregate transitions are also a good moment to re-pull the
          // device list — a new TV may have just claimed a code, or a row
          // may have been removed remotely (e.g. from another parent
          // surface).
          refreshDevices();
        }
      },
      [refreshDevices],
    ),
  });

  const activeChild =
    children.find((c) => c.id === activeChildId) ?? children[0] ?? null;
  const activeTheme = activeChild?.theme ?? PROFILE_THEMES[0];
  const stars = activeChild?.progress?.correctAnswers ?? 0;

  const handleStopRemote = useCallback(async () => {
    if (!nowPlaying || !accessToken) return;
    const name =
      children.find((c) => c.id === nowPlaying.childId)?.name ?? "your child";
    const confirmed = await webConfirm(
      `The TV will show a friendly "Time for a break!" screen.`,
      {
        title: `Stop ${name}'s video?`,
        confirmLabel: "Stop video",
        destructive: true,
      },
    );
    if (!confirmed) return;
    sessionsApi.stop(nowPlaying.sessionId, accessToken).catch(() => {});
    setNowPlaying(null);
  }, [nowPlaying, accessToken, children]);

  // Guest tapped "Start free trial" anywhere: stash the intent and route
  // through signup. WebShell's subscription effect activates the trial once
  // they're authed.
  const startGuestTrial = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("kidly-pending-trial", "1");
    }
    nav.navigate("Signup");
  }, [nav]);

  // A premium video a guest tried to open — drives the "this is Kidly+" popup.
  const [premiumPrompt, setPremiumPrompt] = useState<WebVideo | null>(null);

  const onPlay = useCallback((video: WebVideo) => {
    // Guests can SEE premium titles but can't play them — intercept the tap
    // and pitch the free trial instead of opening the (unfetchable) player.
    if (isGuest && video.isPremium) {
      setPremiumPrompt(video);
      return;
    }
    // Keep the show queue only while advancing within it (up-next click /
    // auto-advance to the next episode); playing anything else exits show mode.
    setEpisodeQueue((q) => (q && q.some((e) => e.id === video.id) ? q : null));
    // Remember where playback was launched from so closing the player
    // returns there (e.g. Studio → play → back lands back in Studio, not
    // Home). Falls back to Home for the rare case there's no sane origin.
    setPlayerReturnView((prev) => (view === "player" ? prev : view));
    setActiveVideo(video);
    setView("player");
  }, [view, isGuest]);

  // Open a TV show's info page (seasons + episodes).
  const onOpenSeries = useCallback((seriesId: string) => {
    setActiveSeriesId(seriesId);
    setView("series");
  }, []);

  // Start an episode with the whole show as the auto-advance queue. Closing
  // the player returns to the show's info page.
  const playEpisode = useCallback(
    (episode: WebVideo, episodes: WebVideo[]) => {
      if (isGuest && episode.isPremium) {
        setPremiumPrompt(episode);
        return;
      }
      setEpisodeQueue(episodes);
      setPlayerReturnView("series");
      setActiveVideo(episode);
      setView("player");
    },
    [isGuest],
  );

  const handleSignOut = useCallback(async () => {
    const ok = await webConfirm("You'll need to sign back in to use Kidly.", {
      title: "Sign out?",
      confirmLabel: "Sign out",
      destructive: true,
    });
    if (!ok) return;
    logout();
    nav.replace("Login");
  }, [logout, nav]);

  // Permanently delete the account (the Privacy tab already confirmed), then
  // clear local auth and bounce to Login. Throws on failure so the tab can
  // surface the error and re-enable the button.
  const handleDeleteAccount = useCallback(async () => {
    if (!accessToken) return;
    const { authApi } = await import("../api/auth");
    await authApi.deleteAccount(accessToken);
    logout();
    nav.replace("Login");
  }, [accessToken, logout, nav]);

  // Close profile menu on outside click
  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!profileBtnRef.current?.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [profileMenuOpen]);

  // Close TV devices menu on outside click
  useEffect(() => {
    if (!tvMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!tvPillRef.current?.contains(e.target as Node)) {
        setTvMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [tvMenuOpen]);

  // Multi-TV: disconnect one specific device. The synthetic `__legacy__`
  // row (rendered when /devices is unavailable but /status reports paired)
  // falls back to the all-TVs DELETE /v1/pairing endpoint since we don't
  // have a real PairingCode.id to target.
  const handleUnpairDevice = useCallback(
    async (device: TvDevice) => {
      if (!accessToken) return;
      const ok = await webConfirm(
        `${device.name} will need to be paired again to use Kidly.`,
        {
          title: `Disconnect ${device.name}?`,
          confirmLabel: "Disconnect",
          destructive: true,
        },
      );
      if (!ok) return;
      if (device.id === "__legacy__") {
        await pairingApi.unpair(accessToken).catch(() => {});
      } else {
        await pairingApi
          .unpairDevice(device.id, accessToken)
          .catch(() => {});
      }
      // Optimistic local removal — the gateway will emit a tv_status
      // update shortly that re-confirms via refreshDevices.
      setDevices((prev) => {
        const next = prev.filter((d) => d.id !== device.id);
        // If we just removed the last device, sync the aggregate pill so
        // it flips back to "Pair TV" without waiting for the tv_status
        // round trip, and close the dropdown (now empty).
        if (next.length === 0) {
          setTvStatus({ paired: false, online: false });
          setTvMenuOpen(false);
        }
        return next;
      });
      // If this was the device hosting the current Now Playing session,
      // clear it — the TV is about to drop offline anyway.
      setNowPlaying((prev) =>
        prev && prev.tvId && prev.tvId === device.id ? null : prev,
      );
    },
    [accessToken],
  );

  // Exclude the session the local web player is itself playing — that's not a
  // remote screen to co-watch, it's this same browser.
  const remoteNowPlaying =
    nowPlaying && nowPlaying.sessionId !== localSessionId ? nowPlaying : null;
  const liveCowatch = !!remoteNowPlaying;

  return (
    <div className="w-app">
      {/* Top bar */}
      <div className="w-topbar">
        {/* Hamburger — only visible under 768px (CSS), opens the nav drawer. */}
        <button
          className="w-burger"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 7h16M4 12h16M4 17h16"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="w-brand">
          <img src="/kidly-logo.webp" alt="Kidly" className="w-brand-logo" />
        </div>

        <div className="w-nav-row">
          {/* Guests only get Home — the rest (Studio, Playlists, Co-watch,
              Controls, Activity) are parent-account features behind sign-in. */}
          {(isGuest ? NAV.filter((n) => n.key === "home") : NAV).map((n) => (
            <button
              key={n.key}
              className={"w-navlink " + (view === n.key ? "active" : "")}
              onClick={() => setView(n.key)}
            >
              {n.label}
              {n.key === "cowatch" && liveCowatch && <span className="live" />}
            </button>
          ))}
        </div>

        <div className="w-topright">
          {isGuest ? (
            // Guest: a single Sign in entry point. No TV pill, stars, or
            // profile chip — those are account features. (Signup is still
            // reachable via the toggle on the Sign in page itself.)
            <button
              className="btn"
              onClick={() => nav.navigate("Login")}
              style={{ cursor: "pointer", fontFamily: "inherit" }}
            >
              Sign in
            </button>
          ) : (
            <>
          <TvStatusPill
            devices={devices}
            onPair={() => nav.navigate("PairTV")}
            nowPlaying={nowPlaying}
            childName={
              children.find((c) => c.id === nowPlaying?.childId)?.name
            }
            menuOpen={tvMenuOpen}
            onOpenMenu={() => setTvMenuOpen(true)}
            onCloseMenu={() => setTvMenuOpen(false)}
            onStopSession={() => {
              handleStopRemote();
              setTvMenuOpen(false);
            }}
            onUnpairDevice={handleUnpairDevice}
            pillRef={tvPillRef}
          />
          <button className="w-pill">
            <Icon name="star" size={14} /> {stars}
          </button>
          <div style={{ position: "relative" }} ref={profileBtnRef}>
            <button
              className="w-profile-chip"
              onClick={() => setProfileMenuOpen((o) => !o)}
            >
              <div className="avatar" style={{ background: activeTheme.bg }}>
                <div style={{ marginTop: 6 }}>
                  <WebMascot mood="happy" color={activeTheme.color} size={32} />
                </div>
              </div>
              <span className="w-chip-label">
                {activeChild?.name ?? "Family"}
                <Icon name="chev" size={14} />
              </span>
            </button>
            {profileMenuOpen && (
              <div className="w-profile-menu fadeup">
                <div className="head">Switch profile</div>
                {children.map((c, i) => {
                  const theme = c.theme ?? PROFILE_THEMES[i];
                  return (
                    <button
                      key={c.id}
                      className="item"
                      onClick={() => {
                        setActiveChildId(c.id);
                        setProfileMenuOpen(false);
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: theme.bg,
                          overflow: "hidden",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <div style={{ marginTop: 6 }}>
                          <WebMascot
                            mood="happy"
                            color={theme.color}
                            size={28}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1, textAlign: "left" }}>
                        <div style={{ fontSize: 14 }}>{c.name}</div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "var(--ink-3)",
                          }}
                        >
                          Age {c.age}
                        </div>
                      </div>
                      {c.id === activeChild?.id && (
                        <Icon name="check" size={16} />
                      )}
                    </button>
                  );
                })}
                <div className="divider" />
                <button
                  className="item"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    setView("me");
                  }}
                >
                  <Icon name="family" size={18} /> Me / Account
                </button>
                <button
                  className="item"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    nav.navigate("AddChild");
                  }}
                >
                  <Icon name="plus" size={18} /> Add a child
                </button>
                <button
                  className="item"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    handleSignOut();
                  }}
                  style={{ color: "#FF7A7A" }}
                >
                  <Icon name="logout" size={18} /> Sign out
                </button>
              </div>
            )}
          </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile nav drawer — rendered when the hamburger is tapped. The
          .w-mnav* styles only take visual effect under 768px, but the
          hamburger that opens it is hidden above 768px, so it's mobile-only. */}
      {mobileNavOpen && (
        <>
          <div
            className="w-mnav-backdrop"
            onClick={() => setMobileNavOpen(false)}
          />
          <nav className="w-mnav fadeup">
            <div className="w-mnav-head">
              <img src="/kidly-logo.webp" alt="Kidly" className="w-brand-logo" />
            </div>
            {(isGuest ? NAV.filter((n) => n.key === "home") : NAV).map((n) => (
              <button
                key={n.key}
                className={"w-mnav-link " + (view === n.key ? "active" : "")}
                onClick={() => {
                  setView(n.key);
                  setMobileNavOpen(false);
                }}
              >
                {n.label}
                {n.key === "cowatch" && liveCowatch && <span className="live" />}
              </button>
            ))}
          </nav>
        </>
      )}

      {/* Main */}
      <main className="w-main">
        {loadError && (
          <div
            style={{
              margin: "16px 32px 0",
              padding: "12px 16px",
              borderRadius: 12,
              background: "#FFEDED",
              color: "#C44",
              border: "1px solid #F5C7C7",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {loadError}
          </div>
        )}
        {view === "home" && (
          <WebHome
            child={activeChild}
            kids={children}
            onPlay={onPlay}
            onOpenSeries={onOpenSeries}
            onGoStudio={() => setView("studio")}
            onGoControls={() => setView("controls")}
            onGoCowatch={() => setView("cowatch")}
            liveCowatch={liveCowatch}
            isGuest={isGuest}
            onSignIn={() => nav.navigate("Signup")}
            onStartFreeTrial={startGuestTrial}
          />
        )}
        {view === "controls" &&
          (activeChild ? (
            <WebControls child={activeChild} />
          ) : (
            <NoChildEmpty
              what="controls"
              onAdd={() => nav.navigate("AddChild")}
            />
          ))}
        {view === "activity" &&
          (activeChild ? (
            <WebActivity child={activeChild} />
          ) : (
            <NoChildEmpty
              what="activity"
              onAdd={() => nav.navigate("AddChild")}
            />
          ))}
        {view === "awards" &&
          (activeChild ? (
            <WebStickerBook child={activeChild} />
          ) : (
            <NoChildEmpty
              what="stickers"
              onAdd={() => nav.navigate("AddChild")}
            />
          ))}
        {view === "cowatch" && (
          <WebCoWatch
            child={activeChild}
            nowPlaying={remoteNowPlaying}
            onStop={handleStopRemote}
          />
        )}
        {view === "me" && (
          <WebFamilyAccount
            subscription={subscription}
            kids={children}
            email={emailFromJwt(accessToken)}
            onSignOut={handleSignOut}
            onDeleteAccount={handleDeleteAccount}
            onManageSubscription={async () => {
              if (!accessToken) return;
              try {
                // Existing Stripe customer → hosted Portal (manage card,
                // cancel, resubscribe). Brand-new parent → default-plan
                // checkout (monthly). Plan-specific subscribe is the
                // separate onSubscribe callback below.
                const hasCustomer =
                  subscription && subscription.status !== "inactive";
                const { url } = hasCustomer
                  ? await subscriptionsApi.portal(accessToken)
                  : await subscriptionsApi.checkout(accessToken);
                window.location.href = url;
              } catch (e) {
                webAlert(
                  e instanceof Error
                    ? e.message
                    : "Couldn't reach billing right now.",
                );
              }
            }}
            onSubscribe={async (plan) => {
              if (!accessToken) return;
              try {
                const { url } = await subscriptionsApi.checkout(
                  accessToken,
                  plan,
                );
                window.location.href = url;
              } catch (e) {
                webAlert(
                  e instanceof Error
                    ? e.message
                    : "Couldn't start checkout.",
                );
              }
            }}
            onStartTrial={async () => {
              if (!accessToken) return;
              try {
                // No-card 30-day trial: grants entitlement instantly, no
                // Stripe redirect. Refresh the cached subscription so the
                // panel flips to "active trial" right away.
                const updated = await subscriptionsApi.startTrial(accessToken);
                setSubscription(updated);
              } catch (e) {
                webAlert(
                  e instanceof Error
                    ? e.message
                    : "Couldn't start your free trial.",
                );
              }
            }}
            onSwitchPlan={async (plan) => {
              if (!accessToken) return;
              const updated = await subscriptionsApi.switchPlan(
                plan,
                accessToken,
              );
              // Refresh the cached subscription so the headline + buttons
              // reflect the new plan immediately (the next /me poll would
              // also catch it, but pre-applying feels snappier).
              setSubscription(updated);
            }}
            onPairTV={() => nav.navigate("PairTV")}
            onAddChild={() => nav.navigate("AddChild")}
            onSwitchChild={(id) => setActiveChildId(id)}
            onKidsChanged={() => setKidsVersion((v) => v + 1)}
            onGoAdminMetrics={() => setView("adminMetrics")}
          />
        )}
        {view === "studio" && (
          <WebStudio
            activeChild={activeChild}
            children={children}
            onPlay={onPlay}
          />
        )}
        {view === "playlists" && <WebPlaylists onPlay={onPlay} />}
        {view === "adminMetrics" && <WebAdminMetrics />}
        {view === "series" && activeSeriesId && (
          <WebSeriesDetail
            seriesId={activeSeriesId}
            onPlayEpisode={playEpisode}
            onBack={() => setView("home")}
          />
        )}
        {view === "player" && activeVideo && (
          <WebPlayer
            video={activeVideo}
            activeChildId={activeChild?.id ?? null}
            onPlay={onPlay}
            queue={episodeQueue ?? undefined}
            onLocalSession={handleLocalSession}
            onClose={() => {
              setActiveVideo(null);
              setView(playerReturnView);
            }}
          />
        )}
      </main>

      {/* Guest tapped a premium title — pitch the free trial. */}
      {premiumPrompt && (
        <div
          onClick={() => setPremiumPrompt(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            background: "rgba(20,12,8,0.62)",
            backdropFilter: "blur(3px)",
            display: "grid",
            placeItems: "center",
            padding: 24,
          }}
        >
          <div
            className="fadeup"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface)",
              borderRadius: 26,
              overflow: "hidden",
              maxWidth: 440,
              width: "100%",
              boxShadow: "0 40px 80px rgba(0,0,0,0.45)",
              position: "relative",
            }}
          >
            {/* Close */}
            <button
              onClick={() => setPremiumPrompt(null)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 2,
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: 0,
                background: "rgba(255,255,255,0.25)",
                color: "#fff",
                fontSize: 18,
                fontWeight: 800,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>

            {/* Gradient header with the mascot + Kidly+ badge */}
            <div
              style={{
                background:
                  "linear-gradient(135deg, #FF8A4C 0%, #EC6FA0 100%)",
                padding: "26px 24px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div className="bob">
                <WebMascot mood="cheer" color="#FFFFFF" size={86} />
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "rgba(255,255,255,0.22)",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 12,
                  letterSpacing: "0.1em",
                  padding: "5px 12px",
                  borderRadius: 999,
                }}
              >
                <Icon name="sparkle" size={13} /> KIDLY+
              </span>
            </div>

            {/* Body */}
            <div style={{ padding: "22px 26px 24px" }}>
              <h2
                style={{
                  fontFamily: "var(--display)",
                  fontSize: 23,
                  fontWeight: 900,
                  color: "var(--ink)",
                  margin: "0 0 6px",
                  textAlign: "center",
                  letterSpacing: "-0.01em",
                }}
              >
                Unlock &ldquo;{prettyTitle(premiumPrompt.title)}&rdquo;
              </h2>
              <p
                style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: "var(--ink-2)",
                  lineHeight: 1.5,
                  margin: "0 0 18px",
                  textAlign: "center",
                }}
              >
                Try Kidly+ free for 30 days — no card needed.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 11,
                  marginBottom: 22,
                }}
              >
                {[
                  "Every premium video, unlocked",
                  "Ad-free, always",
                  "Bedtime stories & lullabies",
                  "Learning games & sticker rewards",
                ].map((perk) => (
                  <div
                    key={perk}
                    style={{ display: "flex", alignItems: "center", gap: 11 }}
                  >
                    <span
                      style={{
                        flex: "0 0 auto",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "var(--bg-warm)",
                        color: "var(--primary-ink)",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Icon name="check" size={14} />
                    </span>
                    <span
                      style={{
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: "var(--ink)",
                      }}
                    >
                      {perk}
                    </span>
                  </div>
                ))}
              </div>

              <button
                className="btn"
                style={{
                  width: "100%",
                  padding: "15px 24px",
                  fontSize: 16,
                  justifyContent: "center",
                }}
                onClick={() => {
                  setPremiumPrompt(null);
                  startGuestTrial();
                }}
              >
                Start 30-day free trial
              </button>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--ink-3)",
                  marginTop: 10,
                }}
              >
                No charge today · cancel anytime
              </div>
              <button
                onClick={() => setPremiumPrompt(null)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 14,
                  border: 0,
                  background: "transparent",
                  color: "var(--ink-3)",
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* On-brand alert/confirm modals — replaces window.alert/confirm. */}
      <WebDialogHost />
    </div>
  );
}

// Multi-TV status pill + dropdown. The pill stays compact ("N TV
// connected" / "Pair TV") and clicking it opens a list of every paired
// device. Per-row controls:
//   - online/offline dot (drives off `device.online`, kept fresh by
//     per-device tv_status events upstream)
//   - "Stop session" — shown only on the row whose `device.id ===
//     nowPlaying.tvId`, so the parent can scope the stop to the
//     specific TV running the session. We also accept a `nowPlaying`
//     without a tvId (REST-hydration path) and just don't surface the
//     stop on any row in that case — the global Co-watch view's Stop
//     still works.
//   - "Disconnect" — per-device DELETE upstream.
function TvStatusPill({
  devices,
  onPair,
  nowPlaying,
  childName,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
  onStopSession,
  onUnpairDevice,
  pillRef,
}: {
  devices: TvDevice[];
  onPair: () => void;
  nowPlaying: NowPlaying | null;
  childName?: string;
  menuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onStopSession: () => void;
  onUnpairDevice: (device: TvDevice) => void;
  pillRef: React.RefObject<HTMLDivElement>;
}) {
  // Zero paired devices ⇒ "Pair TV" CTA. The aggregate `tvStatus.paired`
  // flag isn't read directly here; we trust the devices array as the
  // single source of truth (it's already synthesizing a legacy row from
  // /status when needed).
  if (devices.length === 0) {
    return (
      <button
        className="w-pill"
        onClick={onPair}
        title="Pair a TV"
        style={{
          background: "transparent",
          border: "1px solid var(--line-strong)",
          color: "var(--ink)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "inherit",
        }}
      >
        <Icon name="cast" size={14} /> Pair TV
      </button>
    );
  }

  const onlineCount = devices.filter((d) => d.online).length;
  const anyOnline = onlineCount > 0;
  const pillDot = anyOnline ? "#3CC97A" : "#9AA0A6";
  // Compact label: "1 TV connected" / "2 TVs connected" / "2 TVs offline"
  // when nothing is reachable. Singular when there's exactly one paired
  // device regardless of state.
  const label = (() => {
    if (devices.length === 1) {
      return devices[0].online ? "TV connected" : "TV offline";
    }
    if (anyOnline) {
      return `${onlineCount} of ${devices.length} TVs online`;
    }
    return `${devices.length} TVs offline`;
  })();

  return (
    <div ref={pillRef} style={{ position: "relative" }}>
      <button
        className="w-pill"
        onClick={() => (menuOpen ? onCloseMenu() : onOpenMenu())}
        title="View connected devices"
        style={{
          background: menuOpen ? "var(--bg-warm)" : "transparent",
          border: "1px solid var(--line-strong)",
          color: "var(--ink)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: pillDot,
            boxShadow: anyOnline ? `0 0 0 4px ${pillDot}33` : undefined,
          }}
        />
        {label}
      </button>

      {menuOpen && (
        <div className="w-tv-menu fadeup">
          <div className="head">Connected devices</div>

          {devices.map((device, idx) => {
            // A device "owns" the current session iff the backend tagged
            // the events with its id. The 'legacy' bucket (pre-rollout
            // TVs) and our synthetic '__legacy__' row both fall through
            // since we can't attribute a session to them with certainty
            // — surfacing Stop on every row would be misleading, so we
            // only show it on a verified match.
            const ownsSession =
              !!nowPlaying &&
              !!nowPlaying.tvId &&
              nowPlaying.tvId === device.id;
            const dotColor = device.online ? "#3CC97A" : "#9AA0A6";
            return (
              <React.Fragment key={device.id}>
                {idx > 0 && <div className="divider" />}
                <div className="w-tv-device-row">
                  <span className="w-tv-device-icon">
                    <Icon name="cast" size={20} />
                  </span>
                  <div className="w-tv-device-info">
                    <span className="w-tv-device-name">{device.name}</span>
                    <span
                      className="w-tv-device-status"
                      style={{ color: dotColor }}
                    >
                      {device.online ? "● Online" : "● Offline"}
                    </span>
                  </div>
                </div>

                {ownsSession && nowPlaying && (
                  <div className="w-tv-session-row">
                    <div className="w-tv-session-info">
                      <span className="w-tv-session-title">
                        {nowPlaying.videoTitle}
                      </span>
                      {childName && (
                        <span className="w-tv-session-child">
                          Watching: {childName}
                        </span>
                      )}
                    </div>
                    <button
                      className="w-tv-stop-btn"
                      onClick={onStopSession}
                      title="Stop this session"
                    >
                      Stop
                    </button>
                  </div>
                )}

                <button
                  className="item w-tv-unpair-btn"
                  onClick={() => onUnpairDevice(device)}
                  style={{ marginTop: 4 }}
                >
                  <Icon name="unplug" size={14} /> Disconnect
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Shown on Controls / Activity / Co-watch when the account has no children
// yet. Without this the gated routes rendered nothing → blank page.
function NoChildEmpty({
  what,
  onAdd,
}: {
  what: "controls" | "activity" | "cowatch" | "stickers";
  onAdd: () => void;
}) {
  const copy: Record<typeof what, { h: string; p: string }> = {
    controls: {
      h: "No child profile yet",
      p: "Add a child to set screen-time limits, schedules, and allowed categories.",
    },
    activity: {
      h: "Nothing to chart yet",
      p: "Add a child profile and they'll start earning stars here.",
    },
    cowatch: {
      h: "Nothing playing",
      p: "When your child starts a video on the TV, you'll see it here.",
    },
    stickers: {
      h: "No sticker book yet",
      p: "Add a child profile and they'll start earning stickers here.",
    },
  };
  const c = copy[what];
  return (
    <div className="fadeup w-empty" style={{ padding: 80 }}>
      <WebMascot mood="happy" color="#FF8A4C" size={120} />
      <h2>{c.h}</h2>
      <p style={{ maxWidth: 360 }}>{c.p}</p>
      <button className="btn" style={{ marginTop: 18 }} onClick={onAdd}>
        <Icon name="plus" size={16} /> Add a child
      </button>
    </div>
  );
}

export default WebShell;
